/**
 * Журнал загрузок банковских выписок (карточка BL-2026-08-15-2024, §7.1 п. 2).
 *
 * Flow: опрос /api/treasury/bank-statements раз в 60 секунд и сразу после
 *       загрузки (смена refreshKey) → таблица загрузок со статусами →
 *       щелчок по строке открывает карточку.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadBankStatements;
 *   - ./BankStatementCommon: бейдж статуса и форматтеры.
 *
 * Связанные файлы:
 *   - src/BankStatementsSection.jsx — размещает журнал и карточку;
 *   - docs/specs/BL-2026-08-15-2024-bank-statements.md — §6.3.
 *
 * Журнал показывает И неудачные загрузки (`failed`, `duplicate`): вопрос
 * «приносили ли эту выписку» имеет ответ, только если видно всё, что
 * приносили, а не только то, что приняли.
 *
 * Опрос по таймеру и смена фильтров дают запросы, которые могут прийти не
 * в порядке отправки: применяется только ответ на последний
 * (useLatestRequest), иначе поздний ответ по старому отбору встал бы под
 * новые фильтры.
 */

import { useCallback, useEffect, useState } from "react";

import { loadBankStatements } from "./api";
import {
  STATUS_CODES,
  STATUS_META,
  StatusBadge,
  formatCut,
  formatMoment,
  formatPeriod,
  useLatestRequest,
} from "./BankStatementCommon";

/** Период опроса — как у остальных блоков с ручным вводом. */
const POLL_MS = 60000;

/** Размер страницы журнала — умолчание сервера (§6.3). */
const PAGE = 50;

/**
 * Журнал загрузок.
 *
 * @param {number} refreshKey — меняется после загрузки: перечитать сразу
 * @param {Array} parsers — разборщики для фильтра банка (может быть пуст)
 * @param {number|null} selectedId — открытая карточка (подсветка строки)
 * @param {function(number): void} onOpen — открыть карточку загрузки
 */
export default function BankStatementsTable({
  refreshKey = 0,
  parsers = [],
  selectedId = null,
  onOpen,
}) {
  const [filters, setFilters] = useState({ bank: "", status: "" });
  const [offset, setOffset] = useState(0);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const begin = useLatestRequest();

  const load = useCallback(() => {
    const isCurrent = begin();
    loadBankStatements({ ...filters, limit: PAGE, offset })
      .then((d) => {
        if (!isCurrent()) return;
        setReport(d);
        setError("");
      })
      .catch((e) => {
        if (!isCurrent()) return;
        // Прежний список остаётся на экране: устаревший журнал с подписью
        // полезнее пустого.
        setError(e.message);
        console.error("журнал загрузок не загрузился", e);
      });
  }, [begin, filters, offset]);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load, refreshKey]);

  function setFilter(name, value) {
    setOffset(0);
    setFilters((prev) => ({ ...prev, [name]: value }));
  }

  const uploads = report ? report.uploads || [] : [];
  const total = report ? report.total || 0 : 0;
  const shownTo = offset + uploads.length;

  return (
    <section className="card">
      <h2>Загрузки выписок</h2>

      <div className="filters">
        <select
          value={filters.bank}
          onChange={(e) => setFilter("bank", e.target.value)}
          aria-label="фильтр банка"
        >
          <option value="">все банки</option>
          {parsers.map((p) => (
            <option key={p.code} value={p.code}>
              {p.title}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilter("status", e.target.value)}
          aria-label="фильтр статуса"
        >
          <option value="">любой статус</option>
          {STATUS_CODES.map((code) => (
            <option key={code} value={code}>
              {STATUS_META[code].title}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="error">Журнал не обновился: {error}</p>}
      {!report && !error && <p className="muted">Загрузка…</p>}

      {report && uploads.length === 0 && (
        <p className="muted">Загрузок по этому отбору нет.</p>
      )}

      {uploads.length > 0 && (
        <div className="scroll-rows">
          <table className="bs-table">
            <thead>
              <tr>
                <th>Загружена</th>
                <th>Файл</th>
                <th>Банк</th>
                <th>Период</th>
                <th>Срез</th>
                <th className="num">Счетов</th>
                <th className="num">Операций (новых / было)</th>
                <th className="num">Нераспознано</th>
                <th>Статус</th>
                <th>Кто</th>
              </tr>
            </thead>
            <tbody>
              {uploads.map((u) => (
                <tr
                  key={u.id}
                  className={`bs-row-click ${u.id === selectedId ? "bs-row-selected" : ""}`}
                  onClick={() => onOpen(u.id)}
                  title="открыть карточку загрузки"
                >
                  <td>{formatMoment(u.uploaded_at)}</td>
                  <td className="bs-file">{u.file_name}</td>
                  <td>{u.bank_title || u.bank || "—"}</td>
                  <td>{formatPeriod(u.period_from, u.period_to)}</td>
                  <td>{formatCut(u.cut_at, u.cut_precision)}</td>
                  <td className="num">{u.accounts}</td>
                  <td className="num">
                    {u.operations} ({u.operations_new} / {u.operations_known})
                  </td>
                  <td className={`num ${u.unrecognized > 0 ? "warn" : ""}`}>
                    {u.unrecognized}
                  </td>
                  <td>
                    <StatusBadge status={u.status} title={u.status_title} />
                  </td>
                  <td>{u.uploaded_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {report && (
        <p className="muted">
          Показано {uploads.length === 0 ? 0 : offset + 1}–{shownTo} из {total}.{" "}
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            Назад
          </button>{" "}
          <button
            type="button"
            disabled={shownTo >= total}
            onClick={() => setOffset(offset + PAGE)}
          >
            Вперёд
          </button>
        </p>
      )}
    </section>
  );
}
